/**
 * Snackbar — overlay-level surface that surfaces a transient message.
 *
 * Architecturally a sibling of Modal — both are surfaces that float on
 * top of the page. Where Modal is BLOCKING and dialog-like, Snackbar is
 * NON-blocking and time-bound.
 *
 * Two axes, matching Figma:
 *
 *   - kind (structure):
 *       - `compact` — single-line pill (h 48, radius = h/2). Default.
 *       - `base`    — title + multi-line body card. Padding 16, radius 12.
 *       - `big`     — title block + body + media slot.
 *
 *   - tone (color):
 *       - `neutral` (default), `primary`, `good`, `bad`, `alert`.
 *         All paint with `palette.{tone}.container` + `onContainer`.
 *         Neutral uses the `surface3` ramp with `onSurface` text.
 *
 * @format
 */

import {forwardRef, isValidElement, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {Icon} from '../Icon';

export const SNACKBAR_KINDS = ['compact', 'base', 'big'] as const;
export type SnackbarKind = (typeof SNACKBAR_KINDS)[number];

export const SNACKBAR_TONES = ['neutral', 'primary', 'good', 'bad', 'alert'] as const;
export type SnackbarTone = (typeof SNACKBAR_TONES)[number];

const DEFAULT_ICON: Record<SnackbarTone, string> = {
  primary: 'info',
  good: 'check_circle',
  bad: 'error',
  alert: 'warning',
  neutral: 'notifications',
};

interface SnackPalette {
  bg: string;
  fg: string;
  iconColor: string;
}

function paletteFor(theme: import('@mui/material/styles').Theme, tone: SnackbarTone): SnackPalette {
  const p = theme.palette;
  if (tone === 'neutral') {
    return {
      bg: p.surface.surface3,
      fg: p.text.primary,
      iconColor: p.surface.onSurfaceLight,
    };
  }
  const role = p[tone];
  return {
    bg: role.container,
    fg: role.onContainer,
    iconColor: role.main,
  };
}

const CompactPanel = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: SnackbarTone}>(({theme, $tone}) => {
  const p = paletteFor(theme, $tone);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    minHeight: 48,
    padding: theme.spacing(0, 4),
    borderRadius: 24,
    boxSizing: 'border-box',
    backgroundColor: p.bg,
    color: p.fg,
    maxWidth: 720,
    ...text('label.m'),
    lineHeight: 1.2,
  };
});

const CardPanel = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: SnackbarTone}>(({theme, $tone}) => {
  const p = paletteFor(theme, $tone);
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(4),
    borderRadius: 12,
    boxSizing: 'border-box',
    backgroundColor: p.bg,
    color: p.fg,
    maxWidth: 720,
  };
});

const IconSlot = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: SnackbarTone}>(({theme, $tone}) => ({
  color: paletteFor(theme, $tone).iconColor,
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
}));

const Message = styled(Box)(({theme}) => ({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  padding: theme.spacing(2, 0),
}));

const TitleRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  ...text('label.l'),
}));

const BodyText = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  ...text('body.m'),
  opacity: 0.9,
}));

const MediaSlot = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  aspectRatio: '1 / 1',
  width: '100%',
  maxWidth: 200,
  borderRadius: 8,
  backgroundColor: surfaceVar('surface2'),
  color: surfaceVar('onSurfaceLight'),
  overflow: 'hidden',
  flexShrink: 0,
}));

const Actions = styled(Box)(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
}));

const CloseButton = styled('button')(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: 'transparent',
  border: 0,
  color: 'inherit',
  cursor: 'pointer',
  position: 'relative',
  transition: theme.transitions.create(['opacity'], {
    duration: theme.transitions.duration.shorter,
  }),
  opacity: 0.75,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
    opacity: 0,
    transition: 'opacity 0.15s',
    pointerEvents: 'none',
  },
  '&:hover': {opacity: 1},
  '&:hover::before': {opacity: 0.15},
  '&:focus-visible': {
    outline: '2px solid currentColor',
    outlineOffset: 1,
    opacity: 1,
  },
}));

const CardHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
}));

const CardHeaderText = styled(Box)(({theme}) => ({
  flex: '1 1 auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const CardActions = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexShrink: 0,
}));

export interface SnackbarProps {
  children: ReactNode;
  title?: ReactNode;
  media?: ReactNode;
  leftIcon?: string | ReactNode | null | false;
  actions?: ReactNode;
  onDismiss?: () => void;
  kind?: SnackbarKind;
  tone?: SnackbarTone;
  ariaLabel?: string;
  className?: string;
}

function renderIcon(icon: SnackbarProps['leftIcon'], px: number, fallback: string): ReactNode {
  if (icon === null || icon === false) return null;
  if (icon === undefined) return <Icon name={fallback} size={px} color='inherit' />;
  if (typeof icon === 'string') return <Icon name={icon} size={px} color='inherit' />;
  if (isValidElement(icon)) return icon;
  return icon;
}

export const Snackbar = forwardRef<HTMLDivElement, SnackbarProps>(function Snackbar(
  {
    children,
    title,
    media,
    leftIcon,
    actions,
    onDismiss,
    kind = 'compact',
    tone = 'neutral',
    ariaLabel,
    className,
  },
  ref,
) {
  const ariaRole = tone === 'bad' || tone === 'alert' ? 'alert' : 'status';
  const ariaLive = tone === 'bad' || tone === 'alert' ? 'assertive' : 'polite';

  if (kind === 'compact') {
    return (
      <CompactPanel
        ref={ref}
        role={ariaRole}
        aria-live={ariaLive}
        aria-label={ariaLabel}
        $tone={tone}
        className={className}
      >
        <IconSlot $tone={tone}>
          {renderIcon(leftIcon, 20, DEFAULT_ICON[tone])}
        </IconSlot>
        <Message>{children}</Message>
        {actions && <Actions>{actions}</Actions>}
        {onDismiss && (
          <CloseButton type='button' aria-label='Dismiss' onClick={onDismiss}>
            <Icon name='close' size={20} color='inherit' />
          </CloseButton>
        )}
      </CompactPanel>
    );
  }

  return (
    <CardPanel
      ref={ref}
      role={ariaRole}
      aria-live={ariaLive}
      aria-label={ariaLabel}
      $tone={tone}
      className={className}
    >
      <CardHeader>
        <IconSlot $tone={tone}>
          {renderIcon(leftIcon, 22, DEFAULT_ICON[tone])}
        </IconSlot>
        <CardHeaderText>
          {title != null && <TitleRow>{title}</TitleRow>}
          <BodyText>{children}</BodyText>
        </CardHeaderText>
        {(actions || onDismiss) && (
          <CardActions>
            {actions}
            {onDismiss && (
              <CloseButton type='button' aria-label='Dismiss' onClick={onDismiss}>
                <Icon name='close' size={20} color='inherit' />
              </CloseButton>
            )}
          </CardActions>
        )}
      </CardHeader>
      {kind === 'big' && media && <MediaSlot>{media}</MediaSlot>}
    </CardPanel>
  );
});
